/* eslint-disable react/no-unescaped-entities */
/* eslint-disable consistent-return */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Stack,
  Paper,
  Card,
  Fade,
  Tooltip,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  Layers,
  CheckCircle,
  Storage,
  ChatBubbleOutline,
  Cloud,
  Videocam,
  LinkedIn,
  Fingerprint,
  AutoAwesome,
  Bolt,
  Info,
  Check,
  PieChart as PieChartIcon,
  TrendingUp,
  Inbox,
  ArrowForward,
  ChevronRight,
  Security,
  VpnKey,
  Lock,
  Group,
  Dashboard,
  Work,
  PersonAdd,
} from '@mui/icons-material';
import ScrollAnimation from '../utils/ScrollAnimation';
import meruIcon from '../../assets/icons/Untitled.png';

export const FeaturesHero = () => (
  <Box
    sx={{
      pt: { xs: 8, md: 10 },
      pb: { xs: 4, md: 5 },
      bgcolor: 'background.default',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: '10%',
        left: '15%',
        width: 250,
        height: 250,
        bgcolor: 'rgba(32,101,209,0.05)',
        borderRadius: '50%',
        filter: 'blur(50px)',
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: '10%',
        right: '15%',
        width: 250,
        height: 250,
        bgcolor: 'rgba(118,53,220,0.05)',
        borderRadius: '50%',
        filter: 'blur(50px)',
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(rgba(145, 158, 171, 0.15) 1px, transparent 1px)',
        backgroundSize: '30px 30px',
        zIndex: 0,
      }}
    />

    <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
      <ScrollAnimation direction="up">
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: '16px',
            bgcolor: 'rgba(32,101,209,0.08)',
            border: 1,
            borderColor: 'rgba(32,101,209,0.16)',
            mb: 2,
          }}
        >
          <Layers sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography
            variant="caption"
            fontWeight={800}
            color="primary.main"
            sx={{ letterSpacing: 1, textTransform: 'uppercase' }}
          >
            MeruDesk Modules
          </Typography>
        </Box>
        <Typography
          variant="h2"
          color="text.primary"
          mb={2}
          sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, letterSpacing: '-1px' }}
        >
          Built for modern <br />
          <Box
            component="span"
            sx={{
              background: 'linear-gradient(to right, #2065D1, #7635dc)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            talent teams.
          </Box>
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ lineHeight: 1.6, mx: 'auto' }}
        >
          Explore the most comprehensive suite of hiring tools. From top-of-funnel sourcing to
          day-one onboarding, every module is natively integrated.
        </Typography>
      </ScrollAnimation>
    </Container>
  </Box>
);

export const HomeFeatureCards = () => {
  const [activeCard, setActiveCard] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % 3);
    }, 6000);
    return () => clearInterval(timer);
  }, [isPaused]);

  const cards = [
    <Card
      key="card1"
      elevation={0}
      sx={{
        width: '100%',
        p: { xs: 3, md: 4 },
        borderRadius: '24px',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 12px 40px -10px rgba(145,158,171,0.1)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          bgcolor: 'rgba(32,101,209,0.08)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 4 }}
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Box sx={{ flex: 1, width: '100%', minWidth: 0, zIndex: 10 }}>
          <Box sx={{ maxWidth: { md: 400 } }}>
            <Typography
              variant="h4"
              color="text.primary"
              mb={1.5}
              sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, letterSpacing: '-0.5px' }}
            >
              True ATS Integration. <br />
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(to right, #2065D1, #7635dc)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                No more silos.
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5} lineHeight={1.6}>
              MeruDesk doesn't just track applicants. It connects your hiring data directly to your
              core HRIS and IT systems.
            </Typography>
            <Stack spacing={1.5}>
              {[
                'Designed to support integration with HRIS systems',
                'Native integrations with Meet, Teams & Zoom',
                'Enterprise-grade compliance & role-based access',
                'Custom API access for internal tooling',
              ].map((item, i) => (
                <Stack direction="row" alignItems="center" spacing={1.5} key={i}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: 'rgba(32,101,209,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircle color="primary.main" sx={{ fontSize: 10 }} />
                  </Box>
                  <Typography variant="caption" fontWeight={600} color="text.primary">
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: { xs: 260, md: 320 },
              aspectRatio: '1/1',
              height: 'auto',
              mx: 'auto',
              mt: { xs: 2, md: 0 },
              bgcolor: 'rgba(255, 255, 255, 0.4)',
              backgroundImage: 'radial-gradient(rgba(32, 101, 209, 0.15) 2px, transparent 2px)',
              backgroundSize: '20px 20px',
              borderRadius: '32px',
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.8)',
              boxShadow:
                'inset 0 0 30px rgba(255,255,255,0.8), 0 16px 32px -10px rgba(32,101,209,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '50%',
                height: '50%',
                borderRadius: '50%',
                bgcolor: 'rgba(32, 101, 209, 0.1)',
                filter: 'blur(30px)',
                animation: 'pulseGlow 3s infinite alternate',
                '@keyframes pulseGlow': {
                  '0%': { opacity: 0.5, transform: 'scale(0.8)' },
                  '100%': { opacity: 1, transform: 'scale(1.2)' },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '32px',
                border: '1px solid rgba(32,101,209,0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '60%',
                  height: '60%',
                  borderRadius: '50%',
                  border: '1px dashed rgba(32,101,209,0.3)',
                  animation: 'spinOrbit 20s linear infinite',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '90%',
                  height: '90%',
                  borderRadius: '50%',
                  border: '1px dashed rgba(32,101,209,0.2)',
                  animation: 'spinOrbit 30s linear infinite reverse',
                },
                '@keyframes spinOrbit': {
                  '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' },
                },
              }}
            />
            <svg
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0 }}
            >
              <style>{`@keyframes dashFlow { to { stroke-dashoffset: -30; } } .flow-line { stroke: rgba(32, 101, 209, 0.25); stroke-width: 2.5; stroke-dasharray: 8 8; stroke-linecap: round; animation: dashFlow 1.5s linear infinite; }`}</style>
              <line x1="50%" y1="50%" x2="5%" y2="5%" className="flow-line" />
              <line
                x1="50%"
                y1="50%"
                x2="95%"
                y2="5%"
                className="flow-line"
                style={{ animationDirection: 'reverse' }}
              />
              <line x1="50%" y1="50%" x2="5%" y2="95%" className="flow-line" />
              <line
                x1="50%"
                y1="50%"
                x2="95%"
                y2="95%"
                className="flow-line"
                style={{ animationDirection: 'reverse' }}
              />
              <line x1="50%" y1="50%" x2="50%" y2="-5%" className="flow-line" />
              <line
                x1="50%"
                y1="50%"
                x2="50%"
                y2="105%"
                className="flow-line"
                style={{ animationDirection: 'reverse' }}
              />
              <line x1="50%" y1="50%" x2="-5%" y2="50%" className="flow-line" />
              <line
                x1="50%"
                y1="50%"
                x2="105%"
                y2="50%"
                className="flow-line"
                style={{ animationDirection: 'reverse' }}
              />
            </svg>
            {[
              { color: '#7635dc', endTop: '5%', endLeft: '5%', delay: '0s' },
              { color: '#16a34a', endTop: '5%', endLeft: '95%', delay: '1.5s' },
              { color: '#eab308', endTop: '95%', endLeft: '5%', delay: '2s' },
              { color: '#0ea5e9', endTop: '95%', endLeft: '95%', delay: '0.8s' },
              { color: '#3b82f6', endTop: '-5%', endLeft: '50%', delay: '0.5s' },
              { color: '#0a66c2', endTop: '105%', endLeft: '50%', delay: '1.2s' },
              { color: '#f43f5e', endTop: '50%', endLeft: '-5%', delay: '2.5s' },
              { color: '#8b5cf6', endTop: '50%', endLeft: '105%', delay: '1.8s' },
            ].map((particle, i) => (
              <Box
                key={`particle-${i}`}
                sx={{
                  position: 'absolute',
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: particle.color,
                  boxShadow: `0 0 10px ${particle.color}, 0 0 20px ${particle.color}`,
                  zIndex: 15,
                  animation: `travel${i} 3s ease-in-out infinite ${particle.delay}`,
                  [`@keyframes travel${i}`]: {
                    '0%': {
                      top: '50%',
                      left: '50%',
                      opacity: 0,
                      transform: 'translate(-50%, -50%) scale(0.5)',
                    },
                    '20%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
                    '80%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
                    '100%': {
                      top: particle.endTop,
                      left: particle.endLeft,
                      opacity: 0,
                      transform: 'translate(-50%, -50%) scale(0.5)',
                    },
                  },
                }}
              />
            ))}
            <Paper
              elevation={4}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '22%',
                height: '22%',
                borderRadius: '16px',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 20,
                boxShadow:
                  '0 12px 24px 0 rgba(32, 101, 209, 0.4), inset 0 2px 4px rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Dashboard sx={{ fontSize: 24, color: '#fff' }} />
            </Paper>
            {[
              {
                label: 'Zoom',
                icon: <Videocam sx={{ fontSize: 16, color: '#2D8CFF' }} />,
                top: '-2%',
                left: '-2%',
                delay: '0s',
              },
              {
                label: 'Meet',
                icon: <Videocam sx={{ fontSize: 16, color: '#00832D' }} />,
                top: '-2%',
                right: '-2%',
                delay: '1.5s',
              },
              {
                label: 'LinkedIn',
                icon: <LinkedIn sx={{ fontSize: 16, color: '#0a66c2' }} />,
                bottom: '-2%',
                left: '-2%',
                delay: '0.8s',
              },
              {
                label: 'Teams',
                icon: <Group sx={{ fontSize: 16, color: '#5558AF' }} />,
                bottom: '-2%',
                right: '-2%',
                delay: '2.2s',
              },
              {
                label: 'Security',
                icon: <Security sx={{ fontSize: 16, color: '#f43f5e' }} />,
                top: 'calc(50% - 30px)',
                left: '-8%',
                delay: '0.5s',
              },
              {
                label: 'API',
                icon: <Layers sx={{ fontSize: 16, color: '#8b5cf6' }} />,
                top: 'calc(50% - 30px)',
                right: '-8%',
                delay: '1.2s',
              },
              {
                label: 'Meru AI', // Highlights your AI Agent!
                icon: <AutoAwesome sx={{ fontSize: 16, color: '#f43f5e' }} />, // Pink/Rose for AI magic
                top: '-8%',
                left: 'calc(50% - 30px)',
                delay: '2.5s',
              },
              {
                label: 'ISO',
                icon: <CheckCircle sx={{ fontSize: 16, color: '#eab308' }} />,
                bottom: '-8%',
                left: 'calc(50% - 30px)',
                delay: '1.8s',
              },
            ].map((node, i) => (
              <Paper
                key={`node-${i}`}
                elevation={2}
                sx={{
                  position: 'absolute',
                  ...(node.top && { top: node.top }),
                  ...(node.bottom && { bottom: node.bottom }),
                  ...(node.left && { left: node.left }),
                  ...(node.right && { right: node.right }),
                  width: 60,
                  height: 60,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  zIndex: 30,
                  animation: `floatNode 5s ease-in-out infinite ${node.delay}`,
                  '@keyframes floatNode': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-6px)' },
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    animationPlayState: 'paused',
                    transform: 'translateY(-4px) scale(1.05)',
                    boxShadow: '0 12px 24px -8px rgba(145,158,171,0.3)',
                    zIndex: 40,
                  },
                }}
              >
                {node.icon}
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.6rem' }}
                  fontWeight={800}
                  color="text.primary"
                >
                  {node.label}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      </Stack>
    </Card>,

    /* --- CARD 2: AI MATCHING --- */
    <Card
      key="card2"
      elevation={0}
      sx={{
        width: '100%',
        p: { xs: 3, md: 4 },
        borderRadius: '24px',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 12px 40px -10px rgba(145,158,171,0.1)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -100,
          width: 400,
          height: 400,
          bgcolor: 'rgba(139, 92, 246, 0.08)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />
      <Stack
        direction={{ xs: 'column-reverse', md: 'row' }}
        spacing={{ xs: 3, md: 4 }}
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Box
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: { xs: 260, md: 320 },
              aspectRatio: '1/1',
              height: 'auto',
              mx: 'auto',
              mt: { xs: 2, md: 0 },
              bgcolor: 'rgba(255, 255, 255, 0.4)',
              backgroundImage: 'radial-gradient(rgba(139, 92, 246, 0.15) 2px, transparent 2px)',
              backgroundSize: '20px 20px',
              borderRadius: '32px',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              boxShadow:
                'inset 0 0 30px rgba(255,255,255,0.8), 0 16px 32px -10px rgba(139, 92, 246, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              gap: 1.5,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '60%',
                height: '60%',
                borderRadius: '50%',
                bgcolor: 'rgba(139, 92, 246, 0.15)',
                filter: 'blur(40px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '2px',
                bgcolor: '#8b5cf6',
                boxShadow: '0 0 15px 4px rgba(139, 92, 246, 0.5)',
                zIndex: 20,
                animation: 'scanLine 3s ease-in-out infinite alternate',
                '@keyframes scanLine': { '0%': { top: '10%' }, '100%': { top: '90%' } },
              }}
            />
            <Paper
              elevation={4}
              sx={{
                width: '90%',
                p: 1.25,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                zIndex: 10,
                border: '1px solid #8b5cf6',
                bgcolor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Avatar src="https://i.pravatar.cc/100?img=12" sx={{ width: 32, height: 32 }} />
              <Box flex={1}>
                <Typography variant="caption" fontWeight={800} display="block">
                  Jessica Chen
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }} color="text.secondary">
                  Senior Frontend
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: 'rgba(139, 92, 246, 0.1)',
                  color: '#8b5cf6',
                  px: 1,
                  py: 0.25,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <AutoAwesome sx={{ fontSize: 12 }} />
                <Typography variant="caption" fontWeight={800}>
                  98%
                </Typography>
              </Box>
            </Paper>
            <Paper
              elevation={2}
              sx={{
                width: '80%',
                p: 1.25,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                zIndex: 5,
                opacity: 0.8,
                bgcolor: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Avatar src="https://i.pravatar.cc/100?img=15" sx={{ width: 28, height: 28 }} />
              <Box flex={1}>
                <Typography variant="caption" fontWeight={700} display="block">
                  Marcus Johnson
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }} color="text.secondary">
                  React Developer
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: 'divider',
                  color: 'text.secondary',
                  px: 1,
                  py: 0.25,
                  borderRadius: '6px',
                }}
              >
                <Typography variant="caption" fontWeight={800}>
                  85%
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
        <Box sx={{ flex: 1, width: '100%', minWidth: 0, zIndex: 10 }}>
          <Box sx={{ maxWidth: { md: 400 } }}>
            <Typography
              variant="h4"
              color="text.primary"
              mb={1.5}
              sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, letterSpacing: '-0.5px' }}
            >
              AI Pre-Screening. <br />
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(to right, #8b5cf6, #db2777)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Automate calls.
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5} lineHeight={1.6}>
              Eliminate hours of manual phone screens. Our AI voice agent conducts initial
              conversational interviews to evaluate candidate skills.
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: 'rgba(139, 92, 246, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Bolt sx={{ fontSize: 10, color: '#8b5cf6' }} />
                </Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.primary"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  AI Voice Agent (MeruBot)
                  <Tooltip
                    title={
                      <>
                        MeruBot acts as an automated recruiter, conducting real-time conversational
                        phone screens.
                        <br />
                        <br />
                        <a href="#" style={{ color: '#fff', textDecoration: 'underline' }}>
                          Learn more about MeruBot
                        </a>
                      </>
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 1,
                        cursor: 'help',
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      <Info sx={{ fontSize: 14 }} />
                    </Box>
                  </Tooltip>
                </Typography>
              </Stack>
              {[
                'Real-time transcripts & call summaries',
                'Automated calendar scheduling',
                'Standardized communication evaluation',
              ].map((item, i) => (
                <Stack direction="row" alignItems="center" spacing={1.5} key={i}>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      bgcolor: 'rgba(139, 92, 246, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Check sx={{ fontSize: 10, color: '#8b5cf6' }} />
                  </Box>
                  <Typography variant="caption" fontWeight={600} color="text.primary">
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      </Stack>
    </Card>,

    /* --- CARD 3: GLOBAL ANALYTICS --- */
    <Card
      key="card3"
      elevation={0}
      sx={{
        width: '100%',
        p: { xs: 3, md: 4 },
        borderRadius: '24px',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 12px 40px -10px rgba(145,158,171,0.1)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          bgcolor: 'rgba(217, 119, 6, 0.08)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 4 }}
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Box sx={{ flex: 1, width: '100%', minWidth: 0, zIndex: 10 }}>
          <Box sx={{ maxWidth: { md: 400 } }}>
            <Typography
              variant="h4"
              color="text.primary"
              mb={1.5}
              sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, letterSpacing: '-0.5px' }}
            >
              Global Analytics. <br />
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(to right, #d97706, #ea580c)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Actionable insights.
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5} lineHeight={1.6}>
              Make data-driven decisions with customizable dashboards. Track every metric from
              source-of-hire to diversity stats across your entire organization in real-time.
            </Typography>
            <Stack spacing={1.5}>
              {[
                'Customizable Executive Dashboards',
                'Real-time Time-to-hire Tracking',
                'Comprehensive DE&I Reporting',
                'Automated Scheduled Reports',
              ].map((item, i) => (
                <Stack direction="row" alignItems="center" spacing={1.5} key={i}>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      bgcolor: 'rgba(217, 119, 6, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PieChartIcon sx={{ fontSize: 10, color: '#d97706' }} />
                  </Box>
                  <Typography variant="caption" fontWeight={600} color="text.primary">
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: { xs: 260, md: 320 },
              aspectRatio: '1/1',
              height: 'auto',
              mx: 'auto',
              mt: { xs: 3, md: 0 },
              bgcolor: 'rgba(255, 255, 255, 0.4)',
              backgroundImage: 'radial-gradient(rgba(217, 119, 6, 0.15) 2px, transparent 2px)',
              backgroundSize: '20px 20px',
              borderRadius: '32px',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              boxShadow:
                'inset 0 0 30px rgba(255,255,255,0.8), 0 16px 32px -10px rgba(217, 119, 6, 0.1)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-evenly',
              p: { xs: 1.5, sm: 2.5 },
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '60%',
                height: '60%',
                borderRadius: '50%',
                bgcolor: 'rgba(217, 119, 6, 0.15)',
                filter: 'blur(40px)',
              }}
            />
            {[40, 60, 45, 90, 65, 80].map((h, i) => (
              <Box
                key={i}
                sx={{
                  width: { xs: 16, sm: 24 },
                  height: `${h}%`,
                  bgcolor: i === 3 ? '#d97706' : 'rgba(217, 119, 6, 0.2)',
                  borderRadius: '6px 6px 0 0',
                  position: 'relative',
                  zIndex: 10,
                  animation: `growBar 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards ${i * 0.1}s`,
                  transformOrigin: 'bottom',
                  opacity: 0,
                  '@keyframes growBar': {
                    '0%': { transform: 'scaleY(0)', opacity: 0 },
                    '100%': { transform: 'scaleY(1)', opacity: 1 },
                  },
                }}
              />
            ))}
            <Paper
              elevation={4}
              sx={{
                position: 'absolute',
                top: '15%',
                left: '-5%',
                p: 1,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                zIndex: 20,
                animation: 'floatStat 4s ease-in-out infinite',
                '@keyframes floatStat': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-8px)' },
                },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '8px',
                  bgcolor: 'rgba(34, 197, 94, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TrendingUp sx={{ fontSize: 14, color: '#16a34a' }} />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={800}
                  color="text.primary"
                  sx={{ lineHeight: 1 }}
                >
                  +42%
                </Typography>
              </Box>
            </Paper>
            <Paper
              elevation={4}
              sx={{
                position: 'absolute',
                top: '45%',
                right: '-5%',
                p: 1,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                zIndex: 20,
                animation: 'floatStat 5s ease-in-out infinite 1s',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '8px',
                  bgcolor: 'rgba(217, 119, 6, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PieChartIcon sx={{ fontSize: 14, color: '#d97706' }} />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={800}
                  color="text.primary"
                  sx={{ lineHeight: 1 }}
                >
                  $2.4k
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Stack>
    </Card>,
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 5 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <ScrollAnimation direction="up">
          <Box
            sx={{ position: 'relative', width: '100%', minHeight: { xs: 480, md: 340, lg: 360 } }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {cards.map((card, idx) => (
              <Box
                key={idx}
                sx={{
                  opacity: activeCard === idx ? 1 : 0,
                  pointerEvents: activeCard === idx ? 'auto' : 'none',
                  position: activeCard === idx ? 'relative' : 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transition: 'all 0.8s ease',
                  zIndex: activeCard === idx ? 10 : 0,
                }}
              >
                {card}
              </Box>
            ))}
          </Box>
          <Stack direction="row" spacing={1.5} justifyContent="center" mt={3}>
            {[0, 1, 2].map((idx) => (
              <Box
                key={idx}
                onClick={() => setActiveCard(idx)}
                sx={{
                  width: activeCard === idx ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: activeCard === idx ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.4s ease',
                }}
              />
            ))}
          </Stack>
        </ScrollAnimation>
      </Container>
    </Box>
  );
};

export const BentoFeatures = () => {
  const bentoItems = [
    {
      id: 'ats',
      icon: <Work sx={{ fontSize: 24, color: '#7635dc' }} />,
      bg: 'rgba(118, 53, 220, 0.08)',
      shadow: 'rgba(118, 53, 220, 0.24)',
      title: 'Advanced ATS',
      desc: 'Customize pipelines, schedule interviews, and collaborate with hiring managers seamlessly.',
      span: '66.666%',
      visual: (
        <Box
          sx={{
            position: 'absolute',
            bottom: -10,
            right: -10,
            width: 240,
            height: 140,
            bgcolor: 'background.default',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 12px 40px -12px rgba(118, 53, 220, 0.2)',
            p: 1.5,
            display: { xs: 'none', md: 'flex' },
            gap: 1.5,
            transform: 'rotate(-4deg) translateY(15px)',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '.bento-card:hover &': { transform: 'rotate(-2deg) translateY(0)' },
          }}
        >
          <Box sx={{ flex: 1, bgcolor: 'rgba(118, 53, 220, 0.05)', borderRadius: '12px', p: 1 }}>
            <Box
              sx={{
                width: 30,
                height: 4,
                borderRadius: 2,
                bgcolor: '#7635dc',
                mb: 1.5,
                opacity: 0.5,
              }}
            />
            <Box
              sx={{
                width: '100%',
                height: 24,
                borderRadius: '6px',
                bgcolor: 'background.paper',
                mb: 1,
                boxShadow: 1,
              }}
            />
            <Box
              sx={{
                width: '100%',
                height: 24,
                borderRadius: '6px',
                bgcolor: 'background.paper',
                boxShadow: 1,
              }}
            />
          </Box>
          <Box sx={{ flex: 1, bgcolor: 'rgba(34, 197, 94, 0.05)', borderRadius: '12px', p: 1 }}>
            <Box
              sx={{
                width: 30,
                height: 4,
                borderRadius: 2,
                bgcolor: '#16a34a',
                mb: 1.5,
                opacity: 0.5,
              }}
            />
            <Box
              sx={{
                width: '100%',
                height: 24,
                borderRadius: '6px',
                bgcolor: 'background.paper',
                mb: 1,
                boxShadow: 1,
              }}
            />
          </Box>
        </Box>
      ),
    },
    {
      id: 'crm',
      icon: <Group sx={{ fontSize: 24, color: '#2065D1' }} />,
      bg: 'rgba(32, 101, 209, 0.08)',
      shadow: 'rgba(32, 101, 209, 0.24)',
      title: 'Talent CRM',
      desc: 'Build pools and nurture passive candidates with intelligent drip campaigns.',
      span: '33.333%',
      visual: (
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            transform: 'translateY(15px)',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '.bento-card:hover &': { transform: 'translateY(0)' },
          }}
        >
          <AvatarGroup
            max={4}
            sx={{
              '& .MuiAvatar-root': {
                width: 36,
                height: 36,
                border: '2px solid #fff',
                boxShadow: '0 8px 16px rgba(32, 101, 209, 0.2)',
              },
            }}
          >
            <Avatar src="https://i.pravatar.cc/100?img=33" />
            <Avatar src="https://i.pravatar.cc/100?img=44" />
            <Avatar src="https://i.pravatar.cc/100?img=55" />
            <Avatar src="https://i.pravatar.cc/100?img=66" />
          </AvatarGroup>
        </Box>
      ),
    },
    {
      id: 'onboarding',
      icon: <PersonAdd sx={{ fontSize: 24, color: '#16a34a' }} />,
      bg: 'rgba(34, 197, 94, 0.08)',
      shadow: 'rgba(34, 197, 94, 0.24)',
      title: 'Onboarding',
      desc: 'Convert hires to employees instantly with automated workflows.',
      span: '33.333%',
      visual: (
        <Box
          sx={{
            position: 'absolute',
            bottom: -10,
            right: -10,
            width: 200,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            gap: 1,
            transform: 'rotate(-2deg) translateY(15px)',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '.bento-card:hover &': { transform: 'rotate(0deg) translateY(0)' },
          }}
        >
          {[1, 2, 3].map((i) => (
            <Paper
              key={i}
              elevation={2}
              sx={{
                p: 1.25,
                width: '100%',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: 'background.paper',
                opacity: i === 3 ? 0.5 : 1,
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: i !== 3 ? '#16a34a' : 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i !== 3 && <Check sx={{ fontSize: 10, color: '#fff' }} />}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: i !== 3 ? 'text.primary' : 'divider',
                  opacity: 0.2,
                }}
              />
            </Paper>
          ))}
        </Box>
      ),
    },
    {
      id: 'analytics',
      icon: <PieChartIcon sx={{ fontSize: 24, color: '#d97706' }} />,
      bg: 'rgba(245, 158, 11, 0.08)',
      shadow: 'rgba(245, 158, 11, 0.24)',
      title: 'Global Analytics',
      desc: 'Real-time dashboards on time-to-hire, cost-per-hire, and DE&I metrics.',
      span: '66.666%',
      visual: (
        <Box
          sx={{
            position: 'absolute',
            bottom: -10,
            right: -10,
            width: 240,
            height: 140,
            bgcolor: 'background.default',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 12px 40px -12px rgba(245, 158, 11, 0.2)',
            p: 1.5,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'flex-end',
            gap: 1,
            transform: 'rotate(2deg) translateY(15px)',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '.bento-card:hover &': { transform: 'rotate(0deg) translateY(0)' },
          }}
        >
          {[30, 70, 50, 90, 60, 80, 100].map((h, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: `${h}%`,
                bgcolor: i === 6 ? '#d97706' : 'rgba(245, 158, 11, 0.2)',
                borderRadius: '4px 4px 0 0',
                position: 'relative',
              }}
            >
              {i === 6 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'background.paper',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    boxShadow: 2,
                    fontSize: '9px',
                    fontWeight: 800,
                    color: '#d97706',
                  }}
                >
                  +14%
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 5 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {bentoItems.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                flex: { xs: '1 1 100%', md: `1 1 calc(${item.span} - 16px)` },
                display: 'flex',
              }}
            >
              <ScrollAnimation
                direction="up"
                delay={idx * 100}
                sx={{ flex: 1, display: 'flex', width: '100%' }}
              >
                <Card
                  className="bento-card"
                  elevation={0}
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: { xs: 240, md: 260 },
                    position: 'relative',
                    overflow: 'hidden',
                    p: { xs: 2.5, md: 3 },
                    bgcolor: 'background.paper',
                    borderRadius: '20px',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 24px -8px ${item.shadow}`,
                      borderColor: item.shadow.replace('0.24', '0.2'),
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 10,
                      maxWidth: { md: item.span === '66.666%' ? '50%' : '100%' },
                      flex: 1,
                      mb: { xs: 6, md: item.span === '33.333%' ? 10 : 2 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        bgcolor: item.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={800} color="text.primary" mb={1}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      lineHeight={1.5}
                      fontSize="0.85rem"
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                  {item.visual}
                </Card>
              </ScrollAnimation>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export const AdvancedAIFeatures = () => {
  const [activeTab, setActiveTab] = useState(0);

  const advancedFeatures = [
    {
      id: 'meru-bot',
      icon: (
        <Box component="img" src={meruIcon} alt="MeruBot Icon" sx={{ width: 18, height: 18 }} />
      ),
      color: '#f43f5e',
      title: 'MeruBot AI Agent',
      desc: 'Integrate directly with MeruBot to conduct initial technical or behavioral screening calls via voice AI.',
      graphic: (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: 140,
              height: 140,
              borderRadius: '50%',
              border: '2px dashed rgba(244, 63, 94, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'spin 20s linear infinite',
              '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(244, 63, 94, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s infinite reverse',
              }}
            >
              <Box component="img" src={meruIcon} alt="MeruBot" sx={{ width: 28, height: 28 }} />
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      id: 'auto-links',
      icon: <Videocam sx={{ fontSize: 18, color: '#db2777' }} />,
      color: '#db2777',
      title: 'Auto Interview Links',
      desc: 'Automatically generate Teams, Zoom, or Google Meet links when scheduling interviews from within the ATS.',
      graphic: (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 1.5,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '80%',
              maxWidth: 220,
              borderLeft: '4px solid #db2777',
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                bgcolor: 'rgba(219, 39, 119, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Videocam sx={{ fontSize: 14, color: '#db2777' }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700}>
                Interview Scheduled
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }} color="text.secondary">
                Zoom link generated
              </Typography>
            </Box>
          </Paper>
        </Box>
      ),
    },
    {
      id: 'email-parse',
      icon: <Inbox sx={{ fontSize: 18, color: '#0ea5e9' }} />,
      color: '#0ea5e9',
      title: 'Career Email Parsing',
      desc: 'Automatically fetch candidates and parse their resumes directly from your company career email into organized pipelines.',
      graphic: (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 40,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '2px solid divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
              }}
            >
              <Inbox sx={{ fontSize: 18, color: '#0ea5e9' }} />
            </Box>
            <ArrowForward sx={{ fontSize: 18, color: '#637381', transform: 'rotate(90deg)' }} />
            <Box
              sx={{
                width: 140,
                height: 70,
                bgcolor: 'background.paper',
                borderRadius: 3,
                border: '2px solid #0ea5e9',
                display: 'flex',
                flexDirection: 'column',
                p: 1.5,
                boxShadow: '0 8px 16px rgba(14, 165, 233, 0.15)',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'divider' }} />
                <Box sx={{ width: 60, height: 10, borderRadius: 1, bgcolor: 'divider' }} />
              </Box>
              <Box
                sx={{
                  width: 40,
                  height: 6,
                  borderRadius: 1,
                  bgcolor: 'rgba(14, 165, 233, 0.2)',
                  mb: 1.5,
                }}
              />
              <Box
                sx={{
                  mt: 'auto',
                  width: '100%',
                  height: 12,
                  borderRadius: 1,
                  bgcolor: '#0ea5e9',
                  opacity: 0.1,
                }}
              />
            </Box>
          </Stack>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <ScrollAnimation direction="up">
          <Typography
            variant="h3"
            color="text.primary"
            mb={5}
            textAlign="center"
            sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, letterSpacing: '-0.5px' }}
          >
            Powered by next-gen <br />
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(to right, #8b5cf6, #f43f5e)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              AI & Automations
            </Box>
          </Typography>
        </ScrollAnimation>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
            <Stack spacing={1} sx={{ height: '100%', width: '100%' }}>
              {advancedFeatures.map((feat, idx) => {
                const isActive = activeTab === idx;
                return (
                  <ScrollAnimation key={feat.id} direction="right" delay={idx * 100}>
                    <Paper
                      elevation={isActive ? 2 : 0}
                      onClick={() => setActiveTab(idx)}
                      sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        p: 1.5,
                        borderRadius: '16px',
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: isActive ? feat.color : 'divider',
                        bgcolor: isActive ? 'background.paper' : 'transparent',
                        transition: 'all 0.3s ease',
                        boxShadow: isActive ? `0 6px 20px -8px ${feat.color}40` : 'none',
                        '&:hover': {
                          bgcolor: isActive ? 'background.paper' : 'rgba(145, 158, 171, 0.04)',
                        },
                      }}
                    >
                      {isActive && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 4,
                            height: '100%',
                            bgcolor: feat.color,
                          }}
                        />
                      )}
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            bgcolor: isActive ? `${feat.color}15` : 'background.default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {React.cloneElement(feat.icon, {
                            sx: {
                              fontSize: 18,
                              color: isActive ? feat.color : 'text.secondary',
                            },
                          })}
                        </Box>
                        <Box flex={1}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color={isActive ? 'text.primary' : 'text.secondary'}
                            mb={0.25}
                          >
                            {feat.title}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateRows: isActive ? '1fr' : '0fr',
                              transition: 'grid-template-rows 0.3s ease',
                            }}
                          >
                            <Box sx={{ overflow: 'hidden' }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ pt: 0.5, display: 'block' }}
                              >
                                {feat.desc}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            opacity: isActive ? 1 : 0,
                            transform: isActive ? 'translateX(0)' : 'translateX(-10px)',
                            transition: 'all 0.3s ease',
                            display: { xs: 'none', sm: 'block' },
                            pt: 1,
                          }}
                        >
                          <ChevronRight sx={{ fontSize: 16, color: feat.color }} />
                        </Box>
                      </Stack>
                    </Paper>
                  </ScrollAnimation>
                );
              })}
            </Stack>
          </Box>

          <Box sx={{ flex: 1, width: '100%', minWidth: 0, display: 'flex' }}>
            <ScrollAnimation
              direction="left"
              delay={200}
              sx={{ height: '100%', display: 'flex', width: '100%' }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  flex: 1,
                  minHeight: 260,
                  bgcolor: 'background.default',
                  borderRadius: '20px',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 0 30px rgba(0,0,0,0.02)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '150%',
                    height: '150%',
                    background: `radial-gradient(circle, ${advancedFeatures[activeTab].color}15 0%, transparent 60%)`,
                    transition: 'background 0.5s ease',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage:
                      'radial-gradient(rgba(145, 158, 171, 0.1) 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                    zIndex: 0,
                  }}
                />

                <Fade in timeout={500} key={activeTab}>
                  <Box
                    sx={{
                      zIndex: 1,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {advancedFeatures[activeTab].graphic}
                  </Box>
                </Fade>
              </Paper>
            </ScrollAnimation>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export const SecurityBlock = () => {
  const securityFeatures = [
    {
      title: 'ISO 27001 Certified',
      icon: <Security sx={{ fontSize: 24, color: '#3b82f6' }} />,
      desc: 'Internationally recognized standard for information security management and data protection.',
    },
    {
      title: 'GDPR Compliant',
      icon: <VpnKey sx={{ fontSize: 24, color: '#10b981' }} />,
      desc: 'Full control over your data with native right-to-be-forgotten and data residency options.',
    },
    {
      title: 'End-to-End Encryption',
      icon: <Lock sx={{ fontSize: 24, color: '#8b5cf6' }} />,
      desc: "Your candidates' sensitive data and resumes are encrypted at rest (AES-256) and in transit (TLS 1.2+).",
    },
    {
      title: 'Role-Based Access (RBAC)',
      icon: <Group sx={{ fontSize: 24, color: '#f59e0b' }} />,
      desc: 'Granular permission controls ensure users only access the specific data and modules they need.',
    },
  ];

  return (
    <Box
      sx={{ py: { xs: 8, md: 12 }, bgcolor: '#0B0F19', position: 'relative', overflow: 'hidden' }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
        <ScrollAnimation direction="up">
          <Typography
            variant="h3"
            color="white"
            mb={1.5}
            textAlign="center"
            sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, letterSpacing: '-0.5px' }}
          >
            Enterprise-grade security.
          </Typography>
          <Typography
            variant="body2"
            color="rgba(255,255,255,0.7)"
            mb={6}
            textAlign="center"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            Your data is your most valuable asset. We protect it with military-grade encryption and
            strict compliance standards.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 3,
            }}
          >
            {securityFeatures.map((feat, idx) => (
              <Box key={idx} sx={{ height: '100%' }}>
                <ScrollAnimation direction="up" delay={idx * 150} sx={{ height: '100%' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: '100%',
                      p: 2.5,
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        borderColor: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      {feat.icon}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700} color="white" mb={1}>
                      {feat.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="rgba(255,255,255,0.6)"
                      lineHeight={1.6}
                      display="block"
                    >
                      {feat.desc}
                    </Typography>
                  </Paper>
                </ScrollAnimation>
              </Box>
            ))}
          </Box>
        </ScrollAnimation>
      </Container>
    </Box>
  );
};
